import {
  autoUpdate,
  flip,
  hide,
  offset,
  shift,
  size,
  useClick,
  useInteractions,
  useFloating,
  useDismiss,
  useFocus,
} from "@floating-ui/react"
import compact from "lodash-es/compact"
import { useState } from "react"
import { PopoverContext } from "./PopoverContext"
import { PopoverContent } from "./PopoverContent"
import { PopoverTrigger } from "./PopoverTrigger"
import type { PopoverProps } from "./types"

export function Popover({
  children,
  placement = "bottom",
  strategy = "fixed",
  offset: offsetProp = 8,
  size: sizeProp,
  interactions: interactionsProp = [],
  open: openProp,
  onOpenChange: onOpenChangeProp,
  enableAutoHide = false,
  hideOptions,
}: PopoverProps) {
  const [open, setOpen] = useState(false)
  const isControlled = openProp !== undefined
  const handleOpenChange = onOpenChangeProp || setOpen
  const openState = openProp || open

  const floating = useFloating<HTMLElement>({
    whileElementsMounted: openState ? autoUpdate : undefined,
    placement,
    strategy,
    middleware: compact([
      offset(offsetProp),
      flip(),
      shift(),
      size(sizeProp),
      enableAutoHide && hide(hideOptions),
    ]),
    open: openState,
    onOpenChange: handleOpenChange,
  })

  const uncontrolledInteractions = [
    useClick(floating.context, {
      toggle: true,
    }),
    useFocus(floating.context),
  ]

  const interactions = useInteractions(
    compact([
      useDismiss(floating.context),
      ...(!isControlled ? uncontrolledInteractions : []),
      ...interactionsProp,
    ]),
  )

  return (
    <PopoverContext.Provider value={{ floating, interactions, open: openState }}>
      {children}
    </PopoverContext.Provider>
  )
}

Popover.Content = PopoverContent
Popover.Trigger = PopoverTrigger
